import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Select, Table, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  fetchRolePermissionOptions,
  fetchRolePermissions,
  syncRolePermissions
} from '../../utils/users';

import { useIsMobile } from '../../hooks/useIsMobile';

const RolePermissionsTab = ({ isActive }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [rolePermissions, setRolePermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const roleLabel = useCallback(
    (record) => {
      if (i18n.language === 'en') {
        return record.role_name_en || record.role_name_zh || record.role_id;
      }
      return record.role_name_zh || record.role_name_en || record.role_id;
    },
    [i18n.language]
  );

  const permissionLabel = useCallback(
    (permission) => {
      if (i18n.language === 'en') {
        return permission.permission_name_en || permission.permission_name_zh || permission.permission_id;
      }
      return permission.permission_name_zh || permission.permission_name_en || permission.permission_id;
    },
    [i18n.language]
  );

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [listRes, optionsRes] = await Promise.all([
        fetchRolePermissions(),
        fetchRolePermissionOptions()
      ]);
      setRolePermissions(listRes.rolePermissions || []);
      setRoles(optionsRes.roles || []);
      setPermissions(optionsRes.permissions || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;
    loadData();
  }, [isActive, loadData]);

  const groupedRows = useMemo(() => {
    const permissionMap = new Map(
      permissions.map((permission) => [permission.permission_id, permission])
    );

    return roles.map((role) => {
      const assigned = rolePermissions.filter((item) => item.role_id === role.role_id);
      const assignedPermissions = assigned
        .map((item) => permissionMap.get(item.permission_id) || item)
        .sort((a, b) =>
          (a.permission_id || '').localeCompare(b.permission_id || '', undefined, {
            numeric: true
          })
        );

      return {
        role_id: role.role_id,
        role_name_zh: role.role_name_zh,
        role_name_en: role.role_name_en,
        permissionIds: assigned.map((item) => item.permission_id),
        permissions: assignedPermissions
      };
    });
  }, [rolePermissions, roles, permissions]);

  const openCreateModal = () => {
    setEditingRole(null);
    form.resetFields();
    form.setFieldsValue({ permissionIds: [] });
    setModalOpen(true);
  };

  const openEditModal = useCallback(
    (record) => {
      setEditingRole(record);
      form.setFieldsValue({
        roleId: record.role_id,
        permissionIds: record.permissionIds
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = () => {
    setModalOpen(false);
    setEditingRole(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      await syncRolePermissions(values.roleId, values.permissionIds || []);
      message.success(
        editingRole ? t('rolePermissions.updateSuccess') : t('rolePermissions.createSuccess')
      );

      closeModal();
      await loadData();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || t('rolePermissions.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: t('rolePermissions.role'),
        key: 'role',
        width: isMobile ? 100 : 180,
        render: (_, record) => roleLabel(record)
      },
      {
        title: t('rolePermissions.permissions'),
        key: 'permissions',
        ellipsis: isMobile,
        render: (_, record) => {
          if (!record.permissions.length) {
            return t('rolePermissions.noPermissions');
          }

          return record.permissions.map((permission) => permissionLabel(permission)).join('、');
        }
      },
      {
        title: t('rolePermissions.actions'),
        key: 'actions',
        width: 100,
        render: (_, record) => (
          <Button type="link" onClick={() => openEditModal(record)}>
            {t('rolePermissions.edit')}
          </Button>
        )
      }
    ],
    [openEditModal, permissionLabel, roleLabel, t, isMobile]
  );

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          {t('rolePermissions.add')}
        </Button>
      </div>

      <Table
        rowKey="role_id"
        loading={loading}
        columns={columns}
        dataSource={groupedRows}
        pagination={{ pageSize: 10, size: isMobile ? 'small' : 'default' }}
        scroll={{ x: 'max-content' }}
        className="system-table"
      />

      <Modal
        title={editingRole ? t('rolePermissions.editTitle') : t('rolePermissions.addTitle')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        destroyOnClose
        width={isMobile ? '100%' : 640}
        className="system-modal"
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="roleId"
            label={t('rolePermissions.role')}
            rules={[{ required: true, message: t('rolePermissions.roleRequired') }]}
          >
            <Select
              disabled={Boolean(editingRole)}
              placeholder={t('rolePermissions.rolePlaceholder')}
              options={roles.map((role) => ({
                value: role.role_id,
                label: roleLabel(role)
              }))}
            />
          </Form.Item>

          <Form.Item name="permissionIds" label={t('rolePermissions.permissions')}>
            <Select
              mode="multiple"
              placeholder={t('rolePermissions.permissionsPlaceholder')}
              options={permissions.map((permission) => ({
                value: permission.permission_id,
                label: permissionLabel(permission)
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RolePermissionsTab;
